/**
 * Email-code device recovery: pure policy (no I/O, browser-safe).
 *
 * When none of a user's trusted installs has been seen for
 * ACTIVE_APPROVER_WINDOW_DAYS, a pending install that proves itself with its
 * install secret may ask for a one-time code sent to the account email. A valid
 * code trusts that install and revokes the idle trusted rows.
 */

export const RECOVERY_CODE_LENGTH = 6
export const RECOVERY_CODE_TTL_MS = 10 * 60_000
export const RECOVERY_CODE_MAX_ATTEMPTS = 5
export const RECOVERY_CODE_RESEND_COOLDOWN_MS = 60_000
export const RECOVERY_CODE_MAX_PER_HOUR = 3
export const RECOVERY_CODE_MAX_PER_DAY = 6

const HOUR_MS = 60 * 60_000
const DAY_MS = 24 * HOUR_MS

/** Request counters kept on the reserved recovery row. */
export type RecoveryRequestCounters = {
  issuedAt?: string | null
  hourStart?: string | null
  hourCount?: number | null
  dayStart?: string | null
  dayCount?: number | null
}

export type RecoveryRequestDecision =
  | {
      ok: true
      next: { hourStart: string; hourCount: number; dayStart: string; dayCount: number }
    }
  | { ok: false; reason: 'cooldown' | 'hour' | 'day'; retryAfterMs: number }

function elapsedSince(iso: string | null | undefined, nowMs: number): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  // A timestamp in the future counts as "just now" (never as a free reset).
  return Math.max(0, nowMs - t)
}

/** Fixed hour / day windows plus a resend cooldown. */
export function decideRecoveryRequest(
  prev: RecoveryRequestCounters,
  nowMs: number,
): RecoveryRequestDecision {
  const nowIso = new Date(nowMs).toISOString()
  const sinceIssued = elapsedSince(prev.issuedAt, nowMs)
  if (sinceIssued !== null && sinceIssued < RECOVERY_CODE_RESEND_COOLDOWN_MS) {
    return { ok: false, reason: 'cooldown', retryAfterMs: RECOVERY_CODE_RESEND_COOLDOWN_MS - sinceIssued }
  }

  const sinceDay = elapsedSince(prev.dayStart, nowMs)
  const dayOpen = sinceDay !== null && sinceDay < DAY_MS
  const dayCount = dayOpen ? (prev.dayCount ?? 0) : 0
  if (dayOpen && dayCount >= RECOVERY_CODE_MAX_PER_DAY) {
    return { ok: false, reason: 'day', retryAfterMs: DAY_MS - sinceDay }
  }

  const sinceHour = elapsedSince(prev.hourStart, nowMs)
  const hourOpen = sinceHour !== null && sinceHour < HOUR_MS
  const hourCount = hourOpen ? (prev.hourCount ?? 0) : 0
  if (hourOpen && hourCount >= RECOVERY_CODE_MAX_PER_HOUR) {
    return { ok: false, reason: 'hour', retryAfterMs: HOUR_MS - sinceHour }
  }

  return {
    ok: true,
    next: {
      hourStart: hourOpen ? (prev.hourStart as string) : nowIso,
      hourCount: hourCount + 1,
      dayStart: dayOpen ? (prev.dayStart as string) : nowIso,
      dayCount: dayCount + 1,
    },
  }
}

/** Digits only, exactly RECOVERY_CODE_LENGTH long; null otherwise. */
export function normalizeRecoveryCode(raw: unknown): string | null {
  const digits = String(raw ?? '').replace(/\D/g, '')
  return digits.length === RECOVERY_CODE_LENGTH ? digits : null
}

/** "1 minute", "12 minutes", "3 hours" — for rate-limit messages. */
export function formatRetryAfter(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000))
  if (minutes < 90) return `${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.ceil(minutes / 60)
  return `${hours} hours`
}
